/**
 * The second tap after the sticky bar's tap.
 *
 * A phone tap on the bar's "Find your program" starts a client-side navigation
 * to /apply that takes a second or more. The bar is not rendered on /apply, so
 * a SECOND tap aimed at the same spot, a fraction of a second after /apply
 * appears, lands on whatever now sits under the thumb: the /apply footer's
 * Privacy Policy or Contact link, and the man is carried off the application
 * by accident.
 *
 * The bar's own tap arms this guard. For a short window it swallows a TOUCH
 * click on a link that is not the bar's own, and nothing else: a tap on a field
 * or a button of the application is never blocked, keyboard and assistive
 * activation (a click that is not from a touch pointer) is never blocked, and a
 * mouse is never blocked. The guard lives at module level, outside the bar's
 * React tree, so it survives the bar unmounting on /apply.
 *
 * The window ends at the first of: WINDOW_AFTER_TAP_MS after the bar's tap, or
 * WINDOW_AFTER_ARRIVAL_MS after the URL stops being the page the tap started
 * on. Both ends are timers set when the guard is armed (the arrival one when
 * the poll sees the URL move), and `disarm` clears every timer and listener, so
 * the guard cannot stay armed: the hard cap is a single setTimeout that is
 * always set. Going back (popstate) disarms it too.
 */

const WINDOW_AFTER_TAP_MS = 5000
const WINDOW_AFTER_ARRIVAL_MS = 700
const ARRIVAL_POLL_MS = 50
const BAR_SELECTOR = 'a[data-attr="cta-sticky-bar"]'

let disarmCurrent: (() => void) | null = null

/** True when the event is a finger (or pen) tap, not a mouse or a keyboard. */
function isTouchClick(event: MouseEvent): boolean {
  const pointerType = (event as PointerEvent).pointerType
  if (pointerType === "touch" || pointerType === "pen") return true
  // WebKit (iPhone Safari) reports a finger tap's click as pointerType
  // "mouse", so "mouse" is not final: a real click (detail > 0) on a
  // hover-less coarse-pointer device is a finger. A desktop mouse fails the
  // media query and a keyboard or assistive click has detail 0.
  return (
    event.detail > 0 &&
    window.matchMedia("(hover: none) and (pointer: coarse)").matches
  )
}

/** Arm the guard from the bar's own click. A no-op for a mouse or a keyboard. */
export function armStickyBarSecondTapGuard(armingClick: MouseEvent): void {
  if (typeof window === "undefined") return
  if (!isTouchClick(armingClick)) return

  disarmCurrent?.()

  const startPath = window.location.pathname
  let capTimer: ReturnType<typeof setTimeout> | null = null
  let arrivalTimer: ReturnType<typeof setTimeout> | null = null
  let poll: ReturnType<typeof setInterval> | null = null

  const onClick = (event: MouseEvent) => {
    if (!event.isTrusted || !isTouchClick(event)) return
    const target = event.target instanceof Element ? event.target : null
    const link = target?.closest("a[href]") ?? null
    if (!link) return
    // A retry on the bar itself (the navigation failed) is the man's wish.
    if (link.matches(BAR_SELECTOR)) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  const disarm = () => {
    if (capTimer) clearTimeout(capTimer)
    if (arrivalTimer) clearTimeout(arrivalTimer)
    if (poll) clearInterval(poll)
    capTimer = arrivalTimer = poll = null
    document.removeEventListener("click", onClick, true)
    window.removeEventListener("popstate", disarm)
    if (disarmCurrent === disarm) disarmCurrent = null
  }

  document.addEventListener("click", onClick, true)
  window.addEventListener("popstate", disarm)
  capTimer = setTimeout(disarm, WINDOW_AFTER_TAP_MS)
  poll = setInterval(() => {
    if (window.location.pathname === startPath || arrivalTimer) return
    if (poll) clearInterval(poll)
    poll = null
    arrivalTimer = setTimeout(disarm, WINDOW_AFTER_ARRIVAL_MS)
  }, ARRIVAL_POLL_MS)
  disarmCurrent = disarm
}
